import { Navigate, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  PRME_ORGANIZATION_ID,
  Roles,
  SAMPLE_SURVEY_PATH_PARAM_VALUE,
} from "@/shared/utils/constants";
import { useSurveyPermissions } from "@/features/survey/hooks/useSurveyPermissions";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useGetSurveyByIdQuery } from "@/redux/service";
import SurveyManagement from "@/features/survey/management";
import SurveySummary from "@/features/survey/summary";
import { isSurveyAdminResponse } from "@/redux/survey/typeGuards";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";

const SurveyDetails = () => {
  const { surveyId } = useParams();
  const location = useLocation();

  const isSample = location.pathname.includes(SAMPLE_SURVEY_PATH_PARAM_VALUE);
  const { data: survey, isError } = useGetSurveyByIdQuery(
    surveyId
      ? {
          surveyId,
          filters: {
            organization: isSample ? PRME_ORGANIZATION_ID : undefined,
          },
        }
      : skipToken,
  );
  const currentUser = useSelector(selectCurrentUser);
  const {
    canView,
    canEdit,
    isLoading: isLoadingPermissions,
  } = useSurveyPermissions(survey, currentUser);

  if (!survey || isLoadingPermissions || !currentUser) {
    return (
      <div className="flex justify-center full-width">
        <BaseSpinner />
      </div>
    );
  }

  if (isError || !canView) {
    return <Navigate to="/" />;
  }

  if (currentUser.role === Roles.ADMIN.value) {
    if (!isSurveyAdminResponse(survey)) {
      return <Navigate to="/" />;
    }
    return <SurveyManagement survey={survey} />;
  }

  if (canEdit) {
    return <SurveySummary survey={survey} />;
  }

  return <Navigate to={`/reports/${survey.id}/overview`} />;
};

export default SurveyDetails;
